import React from "react";
import { Link as RouterLink } from "react-router-dom";

import Box from "@mui/material/Box";
import Container from "@mui/material/Container";
import Typography from "@mui/material/Typography";
import Grid from "@mui/material/Grid";
import Stack from "@mui/material/Stack";
import Button from "@mui/material/Button";
import { alpha, useTheme } from "@mui/material/styles";
import BoltIcon from "@mui/icons-material/Bolt";
import AccountBalanceWalletOutlinedIcon from "@mui/icons-material/AccountBalanceWalletOutlined";
import PieChartOutlineIcon from "@mui/icons-material/PieChartOutline";
import SavingsOutlinedIcon from "@mui/icons-material/SavingsOutlined";
import TouchAppOutlinedIcon from "@mui/icons-material/TouchAppOutlined";
import ContactlessOutlinedIcon from "@mui/icons-material/ContactlessOutlined";

import { APP_STORE_URL, openAppStore } from "../utils/appStore";

// Instagram's in-app browser can't open App Store links, so every download CTA
// goes through openAppStore() rather than navigating straight to the href.
// Note there is no target="_blank" here either: window.open is a no-op in that
// webview, which is what made the button look dead.
const handleDownloadClick = (event: React.MouseEvent) => {
    event.preventDefault();
    openAppStore();
};

const AppStoreBadge = ({ height = 52 }: { height?: number }) => (
    <Box
        component="a"
        href={APP_STORE_URL}
        onClick={handleDownloadClick}
        sx={{ display: "inline-block", lineHeight: 0 }}
    >
        <Box
            component="img"
            src="/download_app_store_button.svg"
            alt="Download on the App Store"
            sx={{ height, width: "auto", display: "block" }}
        />
    </Box>
);

export default function Landing() {
    const theme = useTheme();

    const features = [
        {
            icon: <TouchAppOutlinedIcon fontSize="medium" />,
            title: "Log expenses in seconds",
            description:
                "Tap, type an amount, done. Logging a purchase takes less time than paying for it.",
        },
        {
            icon: <AccountBalanceWalletOutlinedIcon fontSize="medium" />,
            title: "Know what's safe to spend",
            description:
                "Your remaining budget for the month is front and center every time you open the app.",
        },
        {
            icon: <PieChartOutlineIcon fontSize="medium" />,
            title: "See where it all goes",
            description:
                "Break down your spending by category so you know exactly what's eating your budget.",
        },
        {
            icon: <SavingsOutlinedIcon fontSize="medium" />,
            title: "Hit your savings target",
            description:
                "Set how much you want to save each month and TapSheet works it into what's safe to spend.",
        },
    ];

    const steps = [
        {
            number: "1",
            title: "Download TapSheet",
            description: "Free on the App Store. Set up takes under a minute.",
        },
        {
            number: "2",
            title: "Add your income & savings goal",
            description:
                "TapSheet calculates what you can safely spend this month.",
        },
        {
            number: "3",
            title: "Log as you spend",
            description:
                "A couple of taps per purchase — or automate it with Apple Pay.",
        },
    ];

    return (
        <Box
            sx={{
                minHeight: "100vh",
                display: "flex",
                flexDirection: "column",
                backgroundColor: "#ffffff",
            }}
        >
            {/* Navigation Bar */}
            <Box
                sx={{
                    py: 2,
                    px: { xs: 2, sm: 4 },
                    position: "sticky",
                    top: 0,
                    zIndex: 10,
                    backgroundColor: alpha("#ffffff", 0.9),
                    backdropFilter: "blur(8px)",
                    borderBottom: `1px solid ${theme.palette.divider}`,
                }}
            >
                <Container maxWidth="lg" disableGutters>
                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                        }}
                    >
                        <Box
                            component="img"
                            src="/tapsheet_logo_text.png"
                            alt="TapSheet"
                            sx={{
                                height: { xs: "22px", sm: "26px" },
                                width: "auto",
                                objectFit: "contain",
                            }}
                        />
                        <Button
                            href={APP_STORE_URL}
                            onClick={handleDownloadClick}
                            variant="contained"
                            size="small"
                            sx={{
                                px: 2.5,
                                py: 0.75,
                                fontSize: "0.85rem",
                                borderRadius: "999px",
                            }}
                        >
                            Download
                        </Button>
                    </Box>
                </Container>
            </Box>

            {/* Hero Section */}
            <Box sx={{ backgroundColor: "#ffffff" }}>
                <Container
                    maxWidth="lg"
                    sx={{ pt: { xs: 5, md: 8 }, pb: { xs: 0, md: 8 } }}
                >
                    <Grid
                        container
                        spacing={{ xs: 4, md: 6 }}
                        alignItems="center"
                        justifyContent="center"
                    >
                        <Grid item xs={12} md={6}>
                            <Stack
                                spacing={2.5}
                                alignItems={{ xs: "center", md: "flex-start" }}
                                textAlign={{ xs: "center", md: "left" }}
                            >
                                <Typography
                                    variant="h1"
                                    sx={{
                                        fontWeight: 800,
                                        fontSize: {
                                            xs: "2.1rem",
                                            sm: "2.6rem",
                                            md: "3.2rem",
                                        },
                                        lineHeight: 1.15,
                                        letterSpacing: "-0.02em",
                                    }}
                                >
                                    Always know what's{" "}
                                    <Box
                                        component="span"
                                        sx={{
                                            color: "primary.main",
                                            whiteSpace: "nowrap",
                                        }}
                                    >
                                        safe to spend
                                    </Box>
                                </Typography>
                                <Typography
                                    color="text.secondary"
                                    sx={{
                                        fontSize: {
                                            xs: "1.05rem",
                                            md: "1.2rem",
                                        },
                                        lineHeight: 1.6,
                                        maxWidth: 440,
                                    }}
                                >
                                    TapSheet is the fast, no-fuss budget app.
                                    Log a purchase in seconds and see exactly
                                    how much of this month's budget is left.
                                </Typography>
                                <Box sx={{ pt: 1 }}>
                                    <AppStoreBadge height={56} />
                                </Box>
                                <Typography
                                    variant="caption"
                                    color="text.secondary"
                                >
                                    Free to download · iPhone
                                </Typography>
                            </Stack>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Box
                                sx={{
                                    display: "flex",
                                    justifyContent: "center",
                                    alignItems: "flex-end",
                                }}
                            >
                                <Box
                                    component="img"
                                    src="/phone_screen_1.png"
                                    alt="TapSheet home screen showing total remaining budget, income, expenses, savings, and recent transactions"
                                    sx={{
                                        maxWidth: {
                                            xs: "260px",
                                            sm: "290px",
                                            md: "330px",
                                        },
                                        width: "100%",
                                        height: "auto",
                                        objectFit: "contain",
                                    }}
                                />
                            </Box>
                        </Grid>
                    </Grid>
                </Container>
            </Box>

            {/* Features Section */}
            <Box
                sx={{
                    pt: { xs: 9, md: 10 },
                    pb: { xs: 7, md: 10 },
                    backgroundColor: "#ffffff",
                }}
            >
                <Container maxWidth="lg">
                    <Stack
                        spacing={1.5}
                        alignItems="center"
                        textAlign="center"
                        sx={{ mb: { xs: 4, md: 6 } }}
                    >
                        <Typography
                            variant="h4"
                            component="h2"
                            sx={{
                                fontWeight: 800,
                                fontSize: { xs: "1.6rem", md: "2.125rem" },
                            }}
                        >
                            Budgeting that fits in your pocket
                        </Typography>
                        <Typography
                            color="text.secondary"
                            sx={{ maxWidth: 480, fontSize: "1rem" }}
                        >
                            No spreadsheets, no bank logins, no learning curve.
                            Just the numbers you actually care about.
                        </Typography>
                    </Stack>
                    <Grid container spacing={{ xs: 2, md: 3 }}>
                        {features.map((feature) => (
                            <Grid item xs={12} sm={6} key={feature.title}>
                                <Stack
                                    direction="row"
                                    spacing={2}
                                    sx={{
                                        p: { xs: 2.5, md: 3 },
                                        height: "100%",
                                        borderRadius: 3,
                                        backgroundColor: "background.default",
                                        border: `1px solid ${theme.palette.divider}`,
                                    }}
                                >
                                    <Box
                                        sx={{
                                            width: 44,
                                            height: 44,
                                            flexShrink: 0,
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            borderRadius: 2,
                                            color: "primary.dark",
                                            backgroundColor: alpha(
                                                theme.palette.primary.main,
                                                0.1,
                                            ),
                                        }}
                                    >
                                        {feature.icon}
                                    </Box>
                                    <Box>
                                        <Typography
                                            sx={{
                                                fontWeight: 700,
                                                fontSize: "1.05rem",
                                                mb: 0.5,
                                            }}
                                        >
                                            {feature.title}
                                        </Typography>
                                        <Typography
                                            variant="body2"
                                            color="text.secondary"
                                            sx={{ lineHeight: 1.6 }}
                                        >
                                            {feature.description}
                                        </Typography>
                                    </Box>
                                </Stack>
                            </Grid>
                        ))}
                    </Grid>
                </Container>
            </Box>

            {/* Apple Pay Automation Section */}
            <Box
                sx={{
                    py: { xs: 7, md: 10 },
                    backgroundColor: "background.default",
                    borderTop: `1px solid ${theme.palette.divider}`,
                    borderBottom: `1px solid ${theme.palette.divider}`,
                }}
            >
                <Container maxWidth="md">
                    <Stack spacing={2.5} alignItems="center" textAlign="center">
                        <Box
                            sx={{
                                width: 56,
                                height: 56,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                borderRadius: "50%",
                                color: "primary.dark",
                                backgroundColor: alpha(
                                    theme.palette.primary.main,
                                    0.1,
                                ),
                            }}
                        >
                            <ContactlessOutlinedIcon fontSize="large" />
                        </Box>
                        <Typography
                            variant="h4"
                            component="h2"
                            sx={{
                                fontWeight: 800,
                                fontSize: { xs: "1.6rem", md: "2.125rem" },
                            }}
                        >
                            Tap to pay. Logged automatically.
                        </Typography>
                        <Typography
                            color="text.secondary"
                            sx={{ maxWidth: 520, fontSize: "1rem" }}
                        >
                            Pair TapSheet with our free Apple Shortcuts and
                            every Apple Pay purchase can log itself — or record
                            an expense with a triple tap on the back of your
                            iPhone. No other budget app makes logging this
                            effortless.
                        </Typography>
                        <Stack
                            direction={{ xs: "column", sm: "row" }}
                            spacing={1.5}
                            sx={{ pt: 1, width: { xs: "100%", sm: "auto" } }}
                        >
                            <Button
                                component={RouterLink}
                                to="/shortcuts/apple-pay-automation"
                                variant="outlined"
                                size="large"
                                startIcon={<ContactlessOutlinedIcon />}
                                sx={{ py: 1.25, px: 3, fontWeight: 600 }}
                            >
                                Apple Pay Automation
                            </Button>
                            <Button
                                component={RouterLink}
                                to="/shortcuts/triple-tap"
                                variant="outlined"
                                size="large"
                                startIcon={<BoltIcon />}
                                sx={{ py: 1.25, px: 3, fontWeight: 600 }}
                            >
                                Triple Tap Shortcut
                            </Button>
                        </Stack>
                    </Stack>
                </Container>
            </Box>

            {/* How It Works Section */}
            <Box sx={{ py: { xs: 7, md: 10 }, backgroundColor: "#ffffff" }}>
                <Container maxWidth="md">
                    <Typography
                        variant="h4"
                        component="h2"
                        align="center"
                        sx={{
                            fontWeight: 800,
                            mb: { xs: 4, md: 6 },
                            fontSize: { xs: "1.6rem", md: "2.125rem" },
                        }}
                    >
                        Up and running in a minute
                    </Typography>
                    <Grid container spacing={{ xs: 3, md: 4 }}>
                        {steps.map((step) => (
                            <Grid item xs={12} sm={4} key={step.number}>
                                <Stack
                                    spacing={1.5}
                                    alignItems={{
                                        xs: "flex-start",
                                        sm: "center",
                                    }}
                                    direction={{ xs: "row", sm: "column" }}
                                    textAlign={{ xs: "left", sm: "center" }}
                                >
                                    <Box
                                        sx={{
                                            width: 40,
                                            height: 40,
                                            flexShrink: 0,
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            borderRadius: "50%",
                                            fontWeight: 800,
                                            fontSize: "1.1rem",
                                            color: "#ffffff",
                                            backgroundColor: "primary.main",
                                        }}
                                    >
                                        {step.number}
                                    </Box>
                                    <Box>
                                        <Typography
                                            sx={{
                                                fontWeight: 700,
                                                fontSize: "1.05rem",
                                                mb: 0.5,
                                            }}
                                        >
                                            {step.title}
                                        </Typography>
                                        <Typography
                                            variant="body2"
                                            color="text.secondary"
                                            sx={{ lineHeight: 1.6 }}
                                        >
                                            {step.description}
                                        </Typography>
                                    </Box>
                                </Stack>
                            </Grid>
                        ))}
                    </Grid>
                </Container>
            </Box>

            {/* CTA Section */}
            <Box
                sx={{
                    py: { xs: 7, md: 9 },
                    px: 2,
                    backgroundColor: theme.palette.primary.main,
                    color: "#ffffff",
                }}
            >
                <Container maxWidth="md">
                    <Stack spacing={2.5} alignItems="center" textAlign="center">
                        <Typography
                            variant="h4"
                            component="h2"
                            sx={{
                                fontWeight: 800,
                                fontSize: { xs: "1.7rem", md: "2.125rem" },
                            }}
                        >
                            Start your budget today
                        </Typography>
                        <Typography
                            sx={{
                                opacity: 0.9,
                                fontSize: { xs: "1rem", md: "1.15rem" },
                                maxWidth: 440,
                            }}
                        >
                            Download TapSheet free and know exactly what's left
                            to spend — starting this month.
                        </Typography>
                        <Box sx={{ pt: 1 }}>
                            <AppStoreBadge height={56} />
                        </Box>
                    </Stack>
                </Container>
            </Box>

            {/* Footer */}
            <Box
                sx={{
                    py: 3,
                    px: { xs: 2, sm: 4 },
                    backgroundColor: "background.paper",
                }}
            >
                <Container maxWidth="lg">
                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            gap: { xs: 1, sm: 3 },
                            flexWrap: "wrap",
                        }}
                    >
                        {[
                            { label: "Privacy Policy", to: "/privacy" },
                            { label: "Terms of Service", to: "/terms" },
                            { label: "Support", to: "/support" },
                            { label: "Shortcuts", to: "/shortcuts" },
                        ].map((link) => (
                            <Button
                                key={link.to}
                                component={RouterLink}
                                to={link.to}
                                variant="text"
                                sx={{
                                    color: "text.secondary",
                                    fontSize: "0.875rem",
                                    textTransform: "none",
                                    "&:hover": { color: "text.primary" },
                                }}
                            >
                                {link.label}
                            </Button>
                        ))}
                    </Box>
                </Container>
            </Box>
        </Box>
    );
}
